<template>
    <v-data-table
        class="elevation-0"
        :headers="headers"
        :items="items"
        :loading="isBusy"
        :loading-text="$t('common.loading_table_data')"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="dataTableFooterProps"
    >
        <template v-slot:item.billing_address="{ item }">
            {{ billingAddressString(item.billing_address) }}
        </template>
        <template v-slot:item.actions="{ item }">
            <span class="text-no-wrap">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small @click="onUpdate(item)" v-on="on" class="mr-2">
                            mdi-pencil
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.buyer.update') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small @click="onAddresses(item)" v-on="on" class="mr-2">
                            mdi-home
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.buyer.addresses') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small @click="onDelete(item)" v-on="on">
                            mdi-delete
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.buyer.delete') }}</span>
                </v-tooltip>
            </span>
        </template>
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                    v-model="filter"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="$t('common.filter')"
                    outlined
                    dense
                    hide-details
                />
                <v-spacer></v-spacer>
                <v-dialog v-model="dialog" max-width="800">
                    <template v-slot:activator="{ on }">
                        <v-btn color="success"
                               dark
                               class="mb-2"
                               v-on="on"
                               @click="cleanEditedItem"
                        >
                            {{ $t('button.new_buyer') }}
                        </v-btn>
                    </template>
                    <v-card :loading="isBusy">
                        <v-card-title>
                            <span class="headline">{{ dialogTitle }}</span>
                            <v-spacer/>
                            <v-btn @click="dialog = false" icon>
                                <v-icon>mdi-window-close</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <span class="title">{{ $t('buyers.personal_info') }}</span>
                                <v-row>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.name.$model"
                                            :label="$t('buyers.name')"
                                            :error-messages="nameErrors"
                                            @input="$v.editedItem.name.$touch()"
                                            @blur="$v.editedItem.name.$touch()"
                                            class="required"
                                            required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.ico.$model"
                                            :label="$t('buyers.ico')"
                                            :error-messages="icoErrors"
                                            @input="$v.editedItem.ico.$touch()"
                                            @blur="$v.editedItem.ico.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.dic.$model"
                                            :label="$t('buyers.dic')"
                                            :error-messages="dicErrors"
                                            @input="$v.editedItem.dic.$touch()"
                                            @blur="$v.editedItem.dic.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.phone.$model"
                                            :label="$t('buyers.phone')"
                                            :error-messages="phoneErrors"
                                            @input="$v.editedItem.phone.$touch()"
                                            @blur="$v.editedItem.phone.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.email.$model"
                                            :label="$t('buyers.email')"
                                            :error-messages="emailErrors"
                                            @input="$v.editedItem.email.$touch()"
                                            @blur="$v.editedItem.email.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.website.$model"
                                            :label="$t('buyers.website')"
                                            :error-messages="websiteErrors"
                                            @input="$v.editedItem.website.$touch()"
                                            @blur="$v.editedItem.website.$touch()"
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                            <v-container>
                                <span class="title">{{ $t('buyers.billing_address') }}</span>
                                <v-row>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.street.$model"
                                            :label="$t('address.street')"
                                            :error-messages="billingAddressStreetErrors"
                                            @input="$v.editedItem.billing_address.street.$touch()"
                                            @blur="$v.editedItem.billing_address.street.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.house_number.$model"
                                            :label="$t('address.house_number')"
                                            :error-messages="billingAddressHouseNumberErrors"
                                            @input="$v.editedItem.billing_address.house_number.$touch()"
                                            @blur="$v.editedItem.billing_address.house_number.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.suburb.$model"
                                            :label="$t('address.suburb')"
                                            :error-messages="billingAddressSuburbErrors"
                                            @input="$v.editedItem.billing_address.suburb.$touch()"
                                            @blur="$v.editedItem.billing_address.suburb.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.city.$model"
                                            :label="$t('address.city')"
                                            :error-messages="billingAddressCityErrors"
                                            @input="$v.editedItem.billing_address.city.$touch()"
                                            @blur="$v.editedItem.billing_address.city.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.postal_code.$model"
                                            :label="$t('address.postal_code')"
                                            :error-messages="billingAddressPostalCodeErrors"
                                            @input="$v.editedItem.billing_address.postal_code.$touch()"
                                            @blur="$v.editedItem.billing_address.postal_code.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.country.$model"
                                            :label="$t('address.country')"
                                            :error-messages="billingAddressCountryErrors"
                                            @input="$v.editedItem.billing_address.country.$touch()"
                                            @blur="$v.editedItem.billing_address.country.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-select
                                            v-model="$v.editedItem.billing_address.country_iso_code.$model"
                                            :items="countryIsoCodes"
                                            :label="$t('address.country_iso_code')"
                                            :error-messages="billingAddressCountryIsoCodeErrors"
                                            @input="$v.editedItem.billing_address.country_iso_code.$touch()"
                                            @blur="$v.editedItem.billing_address.country_iso_code.$touch()"
                                            class="required"
                                            required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.phone.$model"
                                            :label="$t('address.phone')"
                                            :error-messages="billingAddressPhoneErrors"
                                            @input="$v.editedItem.billing_address.phone.$touch()"
                                            @blur="$v.editedItem.billing_address.phone.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.billing_address.email.$model"
                                            :label="$t('address.email')"
                                            :error-messages="billingAddressEmailErrors"
                                            @input="$v.editedItem.billing_address.email.$touch()"
                                            @blur="$v.editedItem.billing_address.email.$touch()"
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                            <v-container v-if="editedIndex === -1">
                                <span class="title">{{ $t('buyers.delivery_address') }}</span>
                                <v-row>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.street.$model"
                                            :label="$t('address.street')"
                                            :error-messages="deliveryAddressStreetErrors"
                                            @input="$v.editedItem.delivery_address.street.$touch()"
                                            @blur="$v.editedItem.delivery_address.street.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.house_number.$model"
                                            :label="$t('address.house_number')"
                                            :error-messages="deliveryAddressHouseNumberErrors"
                                            @input="$v.editedItem.delivery_address.house_number.$touch()"
                                            @blur="$v.editedItem.delivery_address.house_number.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.suburb.$model"
                                            :label="$t('address.suburb')"
                                            :error-messages="deliveryAddressSuburbErrors"
                                            @input="$v.editedItem.delivery_address.suburb.$touch()"
                                            @blur="$v.editedItem.delivery_address.suburb.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.city.$model"
                                            :label="$t('address.city')"
                                            :error-messages="deliveryAddressCityErrors"
                                            @input="$v.editedItem.delivery_address.city.$touch()"
                                            @blur="$v.editedItem.delivery_address.city.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.postal_code.$model"
                                            :label="$t('address.postal_code')"
                                            :error-messages="deliveryAddressPostalCodeErrors"
                                            @input="$v.editedItem.delivery_address.postal_code.$touch()"
                                            @blur="$v.editedItem.delivery_address.postal_code.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.country.$model"
                                            :label="$t('address.country')"
                                            :error-messages="deliveryAddressCountryErrors"
                                            @input="$v.editedItem.delivery_address.country.$touch()"
                                            @blur="$v.editedItem.delivery_address.country.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-select
                                            v-model="$v.editedItem.delivery_address.country_iso_code.$model"
                                            :items="countryIsoCodes"
                                            :label="$t('address.country_iso_code')"
                                            :error-messages="deliveryAddressCountryIsoCodeErrors"
                                            @input="$v.editedItem.delivery_address.country_iso_code.$touch()"
                                            @blur="$v.editedItem.delivery_address.country_iso_code.$touch()"
                                            class="required"
                                            required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.phone.$model"
                                            :label="$t('address.phone')"
                                            :error-messages="deliveryAddressPhoneErrors"
                                            @input="$v.editedItem.delivery_address.phone.$touch()"
                                            @blur="$v.editedItem.delivery_address.phone.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.delivery_address.email.$model"
                                            :label="$t('address.email')"
                                            :error-messages="deliveryAddressEmailErrors"
                                            @input="$v.editedItem.delivery_address.email.$touch()"
                                            @blur="$v.editedItem.delivery_address.email.$touch()"
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="blue darken-1" text @click="onClose" :disabled="isBusy">
                                {{ $t('button.close') }}
                            </v-btn>
                            <v-btn color="blue darken-1" text @click="onSave" :disabled="isBusy">
                                {{ $t('button.save') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-toolbar>
        </template>
    </v-data-table>
</template>

<script>
import {API} from "@/api/api";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import DataTableMixin from "@/mixins/DataTableMixin";
import {email, required, requiredIf} from "vuelidate/lib/validators";
import {validationMixin} from "vuelidate";
import _ from "lodash";
import {countries} from "@/enum/countries";

export default {
    name: "BuyersTable",
    mixins: [SnackbarMixin, DataTableMixin, validationMixin],
    props: {
        warehouseId: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        // loading
        isBusyBuyers: false,
        isBusyCreate: false,
        isBusyUpdate: false,
        isBusyDelete: false,
        // table data
        items: [],
        // table filter
        filter: "",
        // create/update dialog
        dialog: false,
        // data table pagination options
        options: {
            page: 1,
            itemsPerPage: 15
        },
        // data table total items
        total: 0,
        // edited user
        editedItem: {
            name: null,
            ico: null,
            dic: null,
            phone: null,
            email: null,
            website: null,
            billing_address: {
                street: null,
                house_number: null,
                suburb: null,
                city: null,
                postal_code: null,
                country: null,
                country_iso_code: null,
                phone: null,
                email: null
            },
            delivery_address: {
                street: null,
                house_number: null,
                suburb: null,
                city: null,
                postal_code: null,
                country: null,
                country_iso_code: null,
                phone: null,
                email: null
            }
        },
        defaultItem: {
            name: null,
            ico: null,
            dic: null,
            phone: null,
            email: null,
            website: null,
            billing_address: {
                street: null,
                house_number: null,
                suburb: null,
                city: null,
                postal_code: null,
                country: null,
                country_iso_code: null,
                phone: null,
                email: null
            },
            delivery_address: {
                street: null,
                house_number: null,
                suburb: null,
                city: null,
                postal_code: null,
                country: null,
                country_iso_code: null,
                phone: null,
                email: null
            }
        },
        editedIndex: -1,
    }),
    validations: {
        editedItem: {
            name: {required},
            ico: {},
            dic: {},
            phone: {},
            email: {email},
            website: {},
            billing_address: {
                street: {},
                house_number: {},
                suburb: {},
                city: {},
                postal_code: {},
                country: {},
                country_iso_code: {required},
                phone: {},
                email: {email}
            },
            delivery_address: {
                street: {},
                house_number: {},
                suburb: {},
                city: {},
                postal_code: {},
                country: {},
                country_iso_code: {
                    requiredIf: requiredIf(function () {
                        return this.editedIndex === -1; // required on create, not when update
                    })
                },
                phone: {},
                email: {email}
            }
        }
    },
    watch: {
        options: {
            handler() {
                this.fetchBuyers()
            },
            deep: true
        },
        filter: {
            handler: _.debounce(function () {
                this.resetServerTablePage(this.fetchBuyers);
            }, 250)
        }
    },
    computed: {
        isBusy() {
            return this.isBusyBuyers || this.isBusyCreate || this.isBusyUpdate || this.isBusyDelete;
        },
        dialogTitle() {
            return this.editedIndex === -1 ? this.$t('buyers.new_buyer') : this.$t('buyers.edit_buyer')
        },
        countryIsoCodes() {
            return countries.map(country => ({
                value: country.code,
                text: country.name + " (" + country.code + ")"
            }));
        },
        headers() {
            return [
                {
                    text: '#',
                    value: 'external_id',
                    sortable: false
                },
                {
                    text: this.$t('buyers.name'),
                    value: 'name',
                    sortable: false
                },
                {
                    text: this.$t('buyers.ico'),
                    value: 'ico',
                    sortable: false
                },
                {
                    text: this.$t('buyers.dic'),
                    value: 'dic',
                    sortable: false
                },
                {
                    text: this.$t('buyers.phone'),
                    value: 'phone',
                    sortable: false
                },
                {
                    text: this.$t('buyers.email'),
                    value: 'email',
                    sortable: false
                },
                {
                    text: this.$t('buyers.billing_address'),
                    value: 'billing_address',
                    sortable: false
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end'
                }
            ];
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.editedItem.name.$dirty) return errors;
            !this.$v.editedItem.name.required && errors.push(this.$t('validation.name.required'));
            return errors;
        },
        icoErrors() {
            return [];
        },
        dicErrors() {
            return [];
        },
        phoneErrors() {
            return [];
        },
        emailErrors() {
            const errors = [];
            if (!this.$v.editedItem.email.$dirty) return errors;
            !this.$v.editedItem.email.email && errors.push(this.$t('validation.email.email'));
            return errors;
        },
        websiteErrors() {
            return [];
        },
        billingAddressStreetErrors() {
            return [];
        },
        billingAddressHouseNumberErrors() {
            return [];
        },
        billingAddressSuburbErrors() {
            return [];
        },
        billingAddressCityErrors() {
            return [];
        },
        billingAddressPostalCodeErrors() {
            return [];
        },
        billingAddressCountryErrors() {
            return [];
        },
        billingAddressCountryIsoCodeErrors() {
            const errors = [];
            if (!this.$v.editedItem.billing_address.country_iso_code.$dirty) return errors;
            !this.$v.editedItem.billing_address.country_iso_code.required && errors.push(this.$t('validation.country_iso_code.required'));
            return errors;
        },
        billingAddressPhoneErrors() {
            return [];
        },
        billingAddressEmailErrors() {
            const errors = [];
            if (!this.$v.editedItem.billing_address.email.$dirty) return errors;
            !this.$v.editedItem.billing_address.email.email && errors.push(this.$t('validation.email.email'));
            return errors;
        },
        deliveryAddressStreetErrors() {
            return [];
        },
        deliveryAddressHouseNumberErrors() {
            return [];
        },
        deliveryAddressSuburbErrors() {
            return [];
        },
        deliveryAddressCityErrors() {
            return [];
        },
        deliveryAddressPostalCodeErrors() {
            return [];
        },
        deliveryAddressCountryErrors() {
            return [];
        },
        deliveryAddressCountryIsoCodeErrors() {
            const errors = [];
            if (!this.$v.editedItem.delivery_address.country_iso_code.$dirty) return errors;
            !this.$v.editedItem.delivery_address.country_iso_code.requiredIf && errors.push(this.$t('validation.country_iso_code.required'));
            return errors;
        },
        deliveryAddressPhoneErrors() {
            return [];
        },
        deliveryAddressEmailErrors() {
            const errors = [];
            if (!this.$v.editedItem.delivery_address.email.$dirty) return errors;
            !this.$v.editedItem.delivery_address.email.email && errors.push(this.$t('validation.email.email'));
            return errors;
        },
    },
    methods: {
        onAddresses(buyer) {
            this.$router.push({name: "buyer-addresses", params: {buyerId: buyer.external_id}});
        },
        onUpdate(buyer) {
            this.editedIndex = this.items.indexOf(buyer);
            this.editedItem = Object.assign({}, buyer);
            this.dialog = true;
        },
        onDelete(buyer) {
            if (!confirm(this.$t('confirm.delete_buyer'))) {
                return;
            }

            this.isBusyDelete = true;
            API.delete('/api/buyers/' + encodeURI(buyer.external_id))
                .then(() => {
                    this.snackbarSuccess(this.$t('snackbar.buyer.deleted'));
                    this.fetchBuyers();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyDelete = false;
                });
        },
        onSave() {
            this.$v.editedItem.$touch();
            if (this.$v.editedItem.$anyError) {
                return;
            }

            if (this.editedIndex > -1) {
                // update
                this.isBusyUpdate = true;
                API.patch('/api/buyers/' + encodeURI(this.items[this.editedIndex].external_id), this.editedItem)
                    .then(() => {
                        this.onClose();
                        this.snackbarSuccess(this.$t('snackbar.buyer.updated'));
                        this.fetchBuyers();
                    })
                    .catch(error => {
                        this.snackbarError(error);
                    })
                    .finally(() => {
                        this.isBusyUpdate = false;
                    })
            } else {
                // create
                this.isBusyCreate = true;
                this.editedItem.warehouse = this.warehouseId;
                API.post('/api/buyers', this.editedItem)
                    .then(() => {
                        this.onClose();
                        this.snackbarSuccess(this.$t('snackbar.buyer.created'));
                        this.fetchBuyers();
                    })
                    .catch(error => {
                        this.snackbarError(error);
                    })
                    .finally(() => {
                        this.isBusyCreate = false;
                    });
            }
        },
        onClose() {
            this.dialog = false;
            setTimeout(() => {
                this.cleanEditedItem();
            }, 300);
        },
        fetchBuyers() {
            this.isBusyBuyers = true;
            API.get('/api/buyers/by-warehouse/' + encodeURI(this.warehouseId), {
                params: {
                    page: this.options.page,
                    perPage: this.options.itemsPerPage,
                    filter: this.filter
                }
            })
                .then(response => {
                    this.items = response.data.items;
                    this.total = response.data.total;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyBuyers = false;
                });
        },
        cleanEditedItem() {
            this.$v.editedItem.$reset();
            this.editedItem = Object.assign({}, this.defaultItem);
            this.editedIndex = -1;
        },
        billingAddressString(address) {
            return [
                address.street,
                address.house_number,
                address.suburb,
                address.city,
                address.postal_code,
                address.country,
                address.country_iso_code
            ].filter(x => !!x).join(', ');
        }
    },
    created() {
        this.fetchBuyers();
    }
}
</script>

<style lang="scss">
div.v-input.required {
    label:after {
        content: " *";
        color: red;
    }
}
</style>
